import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LayoutGridIcon,
  ListIcon,
  MessageCircleIcon,
  PencilIcon,
  PhoneIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  UserCircleIcon,
  XIcon,
} from 'lucide-react';
import type { SmoelenboekEntry, UserRole } from '@kennisbank/shared';
import { Layout, OnlineDot, useOnlineUserIds } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { apiGet, apiPost } from '../api/client.js';
import { initials } from '../lib/format.js';

// w-44 + truncate: een select is zo breed als zijn langste optie, en
// locatienamen als "Woerden oost, schilderskwartier, gezondheidscentrum" duwden
// de hele balk uit elkaar.
const selectClass =
  'w-44 shrink-0 truncate rounded-lg border border-brand-100 bg-white px-2.5 py-2 text-[13px] text-ink-900/80 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400';

function rolLabel(role: UserRole): string {
  switch (role) {
    case 'suppohrt_admin':
      return 'SUPPOHRT Admin';
    case 'suppohrt_medewerker':
    case 'zorggroep_gebruiker':
      return 'POH-GGZ';
    case 'zorggroep_admin':
      return 'Beheerder';
    default:
      return role;
  }
}

function Avatar({
  entry,
  large = false,
}: {
  entry: SmoelenboekEntry;
  large?: boolean;
}): JSX.Element {
  const [imgError, setImgError] = useState(false);
  const online = useOnlineUserIds().has(entry.id);
  const box = large ? 'h-24 w-24 text-[26px]' : 'h-20 w-20 text-[22px]';
  const inner =
    entry.photoUrl && !imgError ? (
      <img
        src={entry.photoUrl}
        alt={entry.displayName}
        onError={() => setImgError(true)}
        className={`${box} rounded-full object-cover ring-2 ring-brand-100`}
      />
    ) : (
      <div
        className={`${box} flex items-center justify-center rounded-full bg-brand-100 ring-2 ring-brand-200`}
      >
        <span className="font-bold text-brand-700">{initials(entry.displayName)}</span>
      </div>
    );
  return (
    <div className="relative inline-block">
      {inner}
      {online && <OnlineDot large={large} />}
    </div>
  );
}

function MedewerkerCard({
  entry,
  onOpen,
  onStartConversation,
  starting,
  isSelf,
}: {
  entry: SmoelenboekEntry;
  onOpen: (entry: SmoelenboekEntry) => void;
  onStartConversation: (userId: string) => void;
  starting: boolean;
  isSelf: boolean;
}): JSX.Element {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(entry);
        }
      }}
      className="flex h-full cursor-pointer flex-col items-center gap-3 rounded-2xl border border-brand-100 bg-white p-5 text-center shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-400"
    >
      <Avatar entry={entry} />
      <div className="min-w-0 w-full">
        <p className="truncate font-semibold text-ink-900">{entry.displayName}</p>
        <p className="mt-0.5 text-[12px] font-medium text-brand-700">
          {entry.functietitel || rolLabel(entry.role)}
        </p>
        {entry.regio && (
          <p className="mt-0.5 truncate text-[12px] text-ink-900/55">{entry.regio}</p>
        )}
        {entry.locatie && <p className="truncate text-[11px] text-ink-900/40">{entry.locatie}</p>}
        {entry.pohGgzSinds && (
          <p className="mt-0.5 text-[11px] text-ink-900/40">
            POH-GGZ sinds {entry.pohGgzSinds.slice(0, 4)}
          </p>
        )}
      </div>

      {entry.specialisaties && entry.specialisaties.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1">
          {entry.specialisaties.slice(0, 4).map((s) => (
            <span
              key={s}
              className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700"
            >
              {s}
            </span>
          ))}
          {entry.specialisaties.length > 4 && (
            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] text-brand-700/70">
              +{entry.specialisaties.length - 4}
            </span>
          )}
        </div>
      )}

      {isSelf ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(entry);
          }}
          className="mt-auto flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-50 px-3 py-2 text-[13px] font-medium text-brand-700 transition-colors hover:bg-brand-100"
        >
          <PencilIcon size={14} />
          Bewerk profiel
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onStartConversation(entry.id);
          }}
          disabled={starting}
          className="mt-auto flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-50 px-3 py-2 text-[13px] font-medium text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-50"
        >
          <MessageCircleIcon size={14} />
          Stuur bericht
        </button>
      )}
    </div>
  );
}

/**
 * Eén regel per collega. Standaardweergave sinds 4 sep 2026 (prototype A):
 * met ~130 accounts is een kaartgrid op mobiel eindeloos scrollen; op desktop
 * blijft de grid beschikbaar via de schakelaar Lijst/Kaarten.
 */
function MedewerkerRij({
  entry,
  onOpen,
  onStartConversation,
  starting,
  isSelf,
}: {
  entry: SmoelenboekEntry;
  onOpen: (entry: SmoelenboekEntry) => void;
  onStartConversation: (userId: string) => void;
  starting: boolean;
  isSelf: boolean;
}): JSX.Element {
  const online = useOnlineUserIds().has(entry.id);
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(entry);
        }
      }}
      className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-brand-50/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-400 sm:px-6"
    >
      <div className="relative shrink-0">
        {entry.photoUrl ? (
          <img src={entry.photoUrl} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-[12px] font-bold text-brand-700">
            {initials(entry.displayName)}
          </div>
        )}
        {online && <OnlineDot />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="truncate text-[14px] font-semibold text-ink-900">{entry.displayName}</p>
          <p className="shrink-0 text-[12px] font-medium text-brand-700">
            {entry.functietitel || rolLabel(entry.role)}
          </p>
        </div>
        {(entry.regio || entry.locatie) && (
          <p className="truncate text-[12px] text-ink-900/55">
            {[entry.regio, entry.locatie].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      {entry.specialisaties && entry.specialisaties.length > 0 && (
        <div className="hidden max-w-[40%] flex-wrap justify-end gap-1 md:flex">
          {entry.specialisaties.slice(0, 4).map((s) => (
            <span
              key={s}
              className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700"
            >
              {s}
            </span>
          ))}
          {entry.specialisaties.length > 4 && (
            <span className="text-[11px] text-ink-900/45">+{entry.specialisaties.length - 4}</span>
          )}
        </div>
      )}
      <button
        type="button"
        aria-label={isSelf ? 'Bewerk profiel' : 'Stuur bericht'}
        onClick={(e) => {
          e.stopPropagation();
          if (isSelf) onOpen(entry);
          else onStartConversation(entry.id);
        }}
        disabled={starting}
        className="shrink-0 rounded-lg bg-brand-50 p-2 text-brand-700 hover:bg-brand-100 disabled:opacity-50"
      >
        {isSelf ? <PencilIcon size={14} /> : <MessageCircleIcon size={14} />}
      </button>
    </li>
  );
}

function DetailField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-900/45">{label}</p>
      <p className="mt-0.5 whitespace-pre-line text-[13px] text-ink-900/80">{value}</p>
    </div>
  );
}

function ProfileDetailModal({
  entry,
  isSelf,
  onClose,
  onStartConversation,
  onEdit,
  starting,
}: {
  entry: SmoelenboekEntry;
  isSelf: boolean;
  onClose: () => void;
  onStartConversation: (userId: string) => void;
  onEdit: () => void;
  starting: boolean;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Profiel van ${entry.displayName}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Avatar entry={entry} large />
            <div className="min-w-0">
              <p className="text-[17px] font-bold text-ink-900">{entry.displayName}</p>
              <p className="text-[12px] font-medium text-brand-700">
                {entry.functietitel || rolLabel(entry.role)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-lg p-1 text-ink-900/40 hover:bg-brand-50 hover:text-ink-900/70"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {(entry.regio || entry.locatie) && (
            <DetailField
              label="Locatie"
              value={[entry.regio, entry.locatie].filter(Boolean).join(' · ')}
            />
          )}
          {entry.woonplaats && <DetailField label="Woonplaats" value={entry.woonplaats} />}
          {entry.pohGgzSinds && <DetailField label="POH-GGZ sinds" value={entry.pohGgzSinds} />}
          {entry.specialisaties && entry.specialisaties.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-900/45">
                Expertise / specialisaties
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {entry.specialisaties.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[12px] font-medium text-brand-700"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {entry.opleidingErvaring && (
            <DetailField label="Opleiding & ervaring" value={entry.opleidingErvaring} />
          )}
          {entry.biografie && <DetailField label="Over mij" value={entry.biografie} />}
          {entry.telefoon && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-900/45">
                Telefoon
              </p>
              <a
                href={`tel:${entry.telefoon.replace(/\s/g, '')}`}
                className="mt-0.5 flex items-center gap-1.5 text-[13px] text-brand-700 hover:underline"
              >
                <PhoneIcon size={13} />
                {entry.telefoon}
              </a>
            </div>
          )}
        </div>

        <div className="mt-6">
          {isSelf ? (
            <button
              type="button"
              onClick={onEdit}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <PencilIcon size={15} />
              Bewerk mijn profiel
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onStartConversation(entry.id)}
              disabled={starting}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              <MessageCircleIcon size={15} />
              Stuur bericht
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function SmoelenboekPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<SmoelenboekEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [startingFor, setStartingFor] = useState<string | null>(null);
  const [selected, setSelected] = useState<SmoelenboekEntry | null>(null);
  const [specialisatie, setSpecialisatie] = useState('');
  const [locatie, setLocatie] = useState('');
  const [alleenOnline, setAlleenOnline] = useState(false);
  const [aflopend, setAflopend] = useState(false);
  // Desktop start op kaarten (besluit Jos 4 sep 2026) en kan naar lijst; mobiel is altijd lijst.
  const [weergave, setWeergave] = useState<'lijst' | 'kaarten'>('kaarten');
  // Op mobiel vullen zoekveld + drie keuzelijsten een half scherm; ingeklapt
  // tot de gebruiker ze nodig heeft.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const onlineIds = useOnlineUserIds();

  useEffect(() => {
    void apiGet<{ users: SmoelenboekEntry[] }>('/api/v1/users/smoelenboek')
      .then((d) => setUsers(d.users))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // `?persoon=<id>` opent direct het profiel (sprekerslink vanuit de agenda).
  const [params, setParams] = useSearchParams();
  const persoonId = params.get('persoon');
  useEffect(() => {
    if (!persoonId || users.length === 0) return;
    const entry = users.find((u) => u.id === persoonId);
    if (entry) openProfile(entry);
    setParams({}, { replace: true });
    // openProfile is stabiel genoeg; alleen de id en de lijst tellen.
  }, [persoonId, users]);

  // `?online=1` (de teller in de header) zet het online-filter meteen aan.
  useEffect(() => {
    if (params.get('online') !== '1') return;
    setAlleenOnline(true);
    setParams({}, { replace: true });
  }, [params, setParams]);

  function openProfile(entry: SmoelenboekEntry): void {
    if (entry.id === user?.id) {
      navigate('/profile');
      return;
    }
    setSelected(entry);
  }

  async function handleStartConversation(userId: string): Promise<void> {
    if (startingFor) return;
    setStartingFor(userId);
    try {
      const res = await apiPost<{ conversation: { id: string } }>('/api/v1/dm/conversations', {
        otherUserId: userId,
      });
      navigate(`/berichten?conv=${res.conversation.id}`);
    } catch {
      setStartingFor(null);
    }
  }

  // Keuzelijsten komen uit de geladen lijst zelf: geen tweede endpoint, en de
  // opties kunnen per definitie niet uit de pas lopen met wat er staat.
  const specialisatieOpties = [...new Set(users.flatMap((u) => u.specialisaties ?? []))].sort(
    (a, b) => a.localeCompare(b, 'nl'),
  );
  const locatieOpties = [
    ...new Set(users.map((u) => u.locatie).filter((l): l is string => !!l)),
  ].sort((a, b) => a.localeCompare(b, 'nl'));
  const filtersActief = !!search.trim() || !!specialisatie || !!locatie || alleenOnline;

  const q = search.trim().toLowerCase();
  const filtered = users
    .filter((u) => {
      if (q) {
        const raak =
          u.displayName.toLowerCase().includes(q) ||
          (u.specialisaties ?? []).some((sp) => sp.toLowerCase().includes(q)) ||
          (u.regio ?? '').toLowerCase().includes(q);
        if (!raak) return false;
      }
      if (specialisatie && !(u.specialisaties ?? []).includes(specialisatie)) return false;
      if (locatie && u.locatie !== locatie) return false;
      // Jezelf eruit: de teller in de header telt collega's, en die teller
      // zet dit filter aan. Anders staat er "3 online" boven vier kaarten.
      if (alleenOnline && (!onlineIds.has(u.id) || u.id === user?.id)) return false;
      return true;
    })
    // De backend sorteert al op naam, maar niet Nederlands-bewust en zonder
    // Z-A. Hier één keer sorteren met localeCompare dekt beide.
    .sort((a, b) =>
      aflopend
        ? b.displayName.localeCompare(a.displayName, 'nl')
        : a.displayName.localeCompare(b.displayName, 'nl'),
    );

  function wisFilters(): void {
    setSearch('');
    setSpecialisatie('');
    setLocatie('');
    setAlleenOnline(false);
  }

  return (
    <Layout>
      <div className="flex h-full flex-col">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          className="flex w-full items-center justify-between border-b border-brand-100 bg-white px-4 py-2.5 text-[13px] font-medium text-ink-900/80 sm:hidden"
        >
          <span className="inline-flex items-center gap-2">
            <SlidersHorizontalIcon size={15} />
            Zoeken en filteren
            {filtersActief && <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />}
          </span>
          <span className="text-[12px] text-ink-900/45">
            {filtered.length} van {users.length}
          </span>
        </button>
        <div
          className={`border-b border-brand-100 bg-white px-4 py-3 sm:block sm:px-6 ${filtersOpen ? '' : 'hidden'}`}
        >
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <div className="relative w-full min-w-0 sm:w-auto sm:flex-1">
              <SearchIcon
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-900/35"
              />
              <input
                type="search"
                placeholder="Zoek op naam of specialisatie…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-brand-100 bg-brand-50/50 py-2 pl-9 pr-3 text-[13px] text-ink-900 placeholder:text-ink-900/40 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>

            <button
              type="button"
              onClick={() => setAlleenOnline((v) => !v)}
              aria-pressed={alleenOnline}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors ${
                alleenOnline
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                  : 'border-brand-100 bg-white text-ink-900/70 hover:bg-brand-50'
              }`}
            >
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500" />
              Alleen online
            </button>

            <select
              value={specialisatie}
              onChange={(e) => setSpecialisatie(e.target.value)}
              aria-label="Filter op specialisatie"
              className={selectClass}
            >
              <option value="">Alle specialisaties</option>
              {specialisatieOpties.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>

            <select
              value={locatie}
              onChange={(e) => setLocatie(e.target.value)}
              aria-label="Filter op locatie"
              className={selectClass}
            >
              <option value="">Alle locaties</option>
              {locatieOpties.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>

            <select
              value={aflopend ? 'za' : 'az'}
              onChange={(e) => setAflopend(e.target.value === 'za')}
              aria-label="Sorteervolgorde"
              className={`${selectClass} w-32`}
            >
              <option value="az">Naam A-Z</option>
              <option value="za">Naam Z-A</option>
            </select>

            {filtersActief && (
              <button
                type="button"
                onClick={wisFilters}
                className="rounded-lg px-2 py-1.5 text-[13px] font-medium text-brand-700 hover:bg-brand-50"
              >
                Wis filters
              </button>
            )}

            <span className="ml-auto hidden shrink-0 text-[12px] text-ink-900/45 sm:inline">
              {filtered.length} van {users.length}
            </span>

            <div className="hidden shrink-0 items-center gap-0.5 rounded-lg border border-brand-100 p-0.5 sm:flex">
              {(
                [
                  ['lijst', ListIcon, 'Lijst'],
                  ['kaarten', LayoutGridIcon, 'Kaarten'],
                ] as const
              ).map(([k, Icon, label]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={weergave === k}
                  aria-label={`Weergave: ${label}`}
                  title={label}
                  onClick={() => setWeergave(k)}
                  className={`rounded-md p-1.5 ${
                    weergave === k
                      ? 'bg-brand-100 text-brand-800'
                      : 'text-ink-900/55 hover:bg-brand-50'
                  }`}
                >
                  <Icon size={15} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto ${weergave === 'kaarten' ? 'sm:p-6' : ''}`}>
          {loading && (
            <div className="flex h-48 items-center justify-center text-[13px] text-ink-900/50">
              Laden…
            </div>
          )}

          {error && (
            <div className="flex h-48 items-center justify-center text-[13px] text-error">
              Kon de lijst niet laden. Probeer de pagina te verversen.
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="flex h-48 flex-col items-center justify-center gap-3 text-center">
              <UserCircleIcon size={36} className="text-ink-900/20" />
              <p className="text-[13px] text-ink-900/50">
                {filtersActief
                  ? 'Geen medewerkers gevonden met deze filters.'
                  : 'Nog geen medewerkers.'}
              </p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <ul
              className={`divide-y divide-brand-100 bg-white ${weergave === 'kaarten' ? 'sm:hidden' : ''}`}
            >
              {filtered.map((entry) => (
                <MedewerkerRij
                  key={entry.id}
                  entry={entry}
                  isSelf={entry.id === user?.id}
                  onOpen={openProfile}
                  onStartConversation={(id) => void handleStartConversation(id)}
                  starting={startingFor === entry.id}
                />
              ))}
            </ul>
          )}

          {!loading && !error && filtered.length > 0 && weergave === 'kaarten' && (
            <div className="hidden auto-rows-fr grid-cols-3 gap-4 sm:grid lg:grid-cols-4 xl:grid-cols-5">
              {filtered.map((entry) => (
                <MedewerkerCard
                  key={entry.id}
                  entry={entry}
                  isSelf={entry.id === user?.id}
                  onOpen={openProfile}
                  onStartConversation={(id) => void handleStartConversation(id)}
                  starting={startingFor === entry.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <ProfileDetailModal
          entry={selected}
          isSelf={selected.id === user?.id}
          onClose={() => setSelected(null)}
          onStartConversation={(id) => void handleStartConversation(id)}
          onEdit={() => navigate('/profile')}
          starting={startingFor === selected.id}
        />
      )}
    </Layout>
  );
}
